import {
    DENTAL_ACCURACY_EXTEND_LABEL,
    DENTAL_ACCURACY_REFINE_LABEL,
    DENTAL_ACCURACY_OUTLINE_LABEL,
    DENTAL_ACCURACY_OVERLAY_LABEL,
} from "../../utils/smileDesign/dentalAccuracyOutlinePrompt";

export type WireframeAttachment = {
    image: string;
    imageName: string;
    saved?: boolean;
};

/** Edited wireframe-on-before composite, mapped to the same after as its wireframe */
export type OverlayAttachment = {
    image: string;
    imageName: string;
    saved?: boolean;
};

/** AI restore: teeth extended to wireframe borders from the placed composite */
export type RestoredAttachment = {
    image: string;
    imageName: string;
    saved?: boolean;
};

export type HistoryItem = {
    image: string;
    smileType: string;
    imageName: string;
    saved?: boolean;
    /** Tooth wireframe filter generated from this after smile */
    wireframe?: WireframeAttachment;
    /** Edited before + wireframe composite for this after */
    overlay?: OverlayAttachment;
    /** Photorealistic restore with teeth extended to wireframe */
    restored?: RestoredAttachment;
};

/** Legacy Box / DB file_type prefix — kept only to ignore old engine uploads */
export const ENGINE_FILE_TYPE_PREFIX = "Engine::";

/** Box / DB file_type prefix for wireframes mapped to an after */
export const WIREFRAME_FILE_TYPE_PREFIX = "Wireframe::";

/** Box / DB file_type prefix for edited overlays mapped to an after */
export const OVERLAY_FILE_TYPE_PREFIX = "Overlay::";

/** Box / DB file_type prefix for AI-extended restores mapped to an after */
export const RESTORED_FILE_TYPE_PREFIX = "Restored::";

/** Box / DB file_type for the case smile-presentation video */
export const SMILE_VIDEO_FILE_TYPE = "Smile_Video";

export function isOverlayFileType(fileType: string): boolean {
    return (
        fileType.startsWith(OVERLAY_FILE_TYPE_PREFIX) ||
        fileType === DENTAL_ACCURACY_OVERLAY_LABEL
    );
}

export function isRestoredFileType(fileType: string): boolean {
    return (
        fileType.startsWith(RESTORED_FILE_TYPE_PREFIX) ||
        fileType === DENTAL_ACCURACY_EXTEND_LABEL ||
        fileType === DENTAL_ACCURACY_REFINE_LABEL
    );
}

export function isSmileVideoFileType(fileType: string): boolean {
    return (
        fileType === SMILE_VIDEO_FILE_TYPE ||
        /^smile[_\s-]?video$/i.test(fileType)
    );
}

export function isWireframeFileType(fileType: string): boolean {
    // Overlays / restores may contain related words — check them first
    if (isOverlayFileType(fileType) || isRestoredFileType(fileType)) return false;
    return (
        fileType.startsWith(WIREFRAME_FILE_TYPE_PREFIX) ||
        fileType === DENTAL_ACCURACY_OUTLINE_LABEL ||
        /wireframe|outline/i.test(fileType)
    );
}

/** After designs only — exclude standalone outline / overlay / restored / video / engine entries */
export function isAfterDesignItem(item: Pick<HistoryItem, "smileType">): boolean {
    return (
        !isWireframeFileType(item.smileType) &&
        !isOverlayFileType(item.smileType) &&
        !isRestoredFileType(item.smileType) &&
        !isSmileVideoFileType(item.smileType) &&
        !isEngineFileType(item.smileType)
    );
}

export function isEngineFileType(fileType: string): boolean {
    return fileType.startsWith(ENGINE_FILE_TYPE_PREFIX);
}

export function wireframeFileTypeForAfter(afterSmileType: string): string {
    return `${WIREFRAME_FILE_TYPE_PREFIX}${afterSmileType}`;
}

export function overlayFileTypeForAfter(afterSmileType: string): string {
    return `${OVERLAY_FILE_TYPE_PREFIX}${afterSmileType}`;
}

export function restoredFileTypeForAfter(afterSmileType: string): string {
    return `${RESTORED_FILE_TYPE_PREFIX}${afterSmileType}`;
}

export function afterSmileTypeFromWireframeFileType(fileType: string): string | null {
    if (fileType.startsWith(WIREFRAME_FILE_TYPE_PREFIX)) {
        return fileType.slice(WIREFRAME_FILE_TYPE_PREFIX.length) || null;
    }
    return null;
}

export function afterSmileTypeFromOverlayFileType(fileType: string): string | null {
    if (fileType.startsWith(OVERLAY_FILE_TYPE_PREFIX)) {
        return fileType.slice(OVERLAY_FILE_TYPE_PREFIX.length) || null;
    }
    return null;
}

export function afterSmileTypeFromRestoredFileType(fileType: string): string | null {
    if (fileType.startsWith(RESTORED_FILE_TYPE_PREFIX)) {
        return fileType.slice(RESTORED_FILE_TYPE_PREFIX.length) || null;
    }
    return null;
}

export function wireframeImageNameForAfter(afterImageName: string): string {
    if (/-image\.(png|jpe?g|webp)$/i.test(afterImageName)) {
        return afterImageName.replace(/-image\.(png|jpe?g|webp)$/i, "-wireframe.png");
    }
    const base = afterImageName.replace(/\.(png|jpe?g|webp)$/i, "");
    return `${base}-wireframe.png`;
}

export function overlayImageNameForAfter(afterImageName: string): string {
    if (/-image\.(png|jpe?g|webp)$/i.test(afterImageName)) {
        return afterImageName.replace(
            /-image\.(png|jpe?g|webp)$/i,
            "-overlay.png"
        );
    }
    const base = afterImageName.replace(/\.(png|jpe?g|webp)$/i, "");
    return `${base}-overlay.png`;
}

export function restoredImageNameForAfter(afterImageName: string): string {
    if (/-image\.(png|jpe?g|webp)$/i.test(afterImageName)) {
        return afterImageName.replace(
            /-image\.(png|jpe?g|webp)$/i,
            "-restored.png"
        );
    }
    const base = afterImageName.replace(/\.(png|jpe?g|webp)$/i, "");
    return `${base}-restored.png`;
}

/** Match saved wireframe filename back to an after imageName */
export function afterImageNameFromWireframeName(wireframeName: string): string | null {
    if (/-wireframe\.(png|jpe?g|webp)$/i.test(wireframeName)) {
        return wireframeName.replace(/-wireframe\.(png|jpe?g|webp)$/i, "-image.png");
    }
    return null;
}

/** Match saved overlay filename back to an after imageName */
export function afterImageNameFromOverlayName(overlayName: string): string | null {
    if (/-overlay\.(png|jpe?g|webp)$/i.test(overlayName)) {
        return overlayName.replace(/-overlay\.(png|jpe?g|webp)$/i, "-image.png");
    }
    if (/-wireframe-on-before\.(png|jpe?g|webp)$/i.test(overlayName)) {
        return overlayName.replace(
            /-wireframe-on-before\.(png|jpe?g|webp)$/i,
            "-image.png"
        );
    }
    return null;
}

/** Match saved restored filename back to an after imageName */
export function afterImageNameFromRestoredName(restoredName: string): string | null {
    if (/-restored\.(png|jpe?g|webp)$/i.test(restoredName)) {
        return restoredName.replace(/-restored\.(png|jpe?g|webp)$/i, "-image.png");
    }
    return null;
}
